import type { GetAuditLogsQueryParams } from '@/generated/http-clients/v3ApiComponents'
import { useMemo } from 'react'

export type DateRangePreset =
  | 'lastHour'
  | 'last24Hours'
  | 'last7Days'
  | 'last30Days'

export type AuditLogStatus = 'success' | 'error'

export interface AuditLogFilterParams {
  page: number
  dateRangePreset?: DateRangePreset
  hiveId?: string | null
  orgId?: string | null
  user?: string | null
  status?: AuditLogStatus | null
}

/**
 * Converts a date range preset to epoch milliseconds for the API
 */
function getDateRangeFromPreset(preset: DateRangePreset): {
  from: number
  to: number
} {
  const now = Date.now()
  const to = now

  switch (preset) {
    case 'lastHour':
      return { from: now - 60 * 60 * 1000, to }
    case 'last24Hours':
      return { from: now - 24 * 60 * 60 * 1000, to }
    case 'last7Days':
      return { from: now - 7 * 24 * 60 * 60 * 1000, to }
    case 'last30Days':
      return { from: now - 30 * 24 * 60 * 60 * 1000, to }
    default:
      return { from: now - 24 * 60 * 60 * 1000, to } // Default to last 24 hours
  }
}

export function useAuditLogFilters(filterParams: AuditLogFilterParams) {
  const {
    page,
    dateRangePreset = 'last24Hours',
    hiveId,
    orgId,
    user,
    status,
  } = filterParams

  // Enhanced query parameters for the API call
  const enhancedQueryParams = useMemo<GetAuditLogsQueryParams>(() => {
    const { from, to } = getDateRangeFromPreset(dateRangePreset)

    const params: GetAuditLogsQueryParams = {
      page,
      from,
      to,
    }

    if (hiveId) {
      params.hiveId = hiveId
    }

    if (orgId) {
      params.orgId = orgId
    }

    if (user) {
      params.user = user
    }

    if (status) {
      params.status = status
    }

    return params
  }, [page, dateRangePreset, hiveId, orgId, user, status])

  // Check if there are any active filters beyond the default date range
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        hiveId || orgId || user || status || dateRangePreset !== 'last24Hours',
      ),
    [hiveId, orgId, user, status, dateRangePreset],
  )

  return {
    enhancedQueryParams,
    hasActiveFilters,
  }
}
